export interface AuthTokenState {
  accessToken: string | null;
  refreshToken: string | null;
  email: string | null;
  userId: string | null;
}

type TokenListener = (state: AuthTokenState) => void;

const storage =
  typeof window !== "undefined" && typeof window.localStorage !== "undefined"
    ? window.localStorage
    : undefined;

const storageKeys = {
  accessToken: "access_token",
  refreshToken: "refresh_token",
  email: "email",
  userId: "user_id",
} as const;

const safeRead = (key: string): string | null => {
  if (!storage) {
    return null;
  }

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeWrite = (key: string, value: string | null): void => {
  if (!storage) {
    return;
  }

  try {
    if (value === null || value === undefined) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, value);
    }
  } catch {
    // no-op: storage might be unavailable (Safari private mode, etc.)
  }
};

const initialState: AuthTokenState = {
  accessToken: safeRead(storageKeys.accessToken),
  refreshToken: safeRead(storageKeys.refreshToken),
  email: safeRead(storageKeys.email),
  userId: safeRead(storageKeys.userId),
};

let tokens: AuthTokenState = { ...initialState };
const listeners = new Set<TokenListener>();

const notify = () => {
  listeners.forEach((listener) => listener(tokens));
};

const persist = () => {
  safeWrite(storageKeys.accessToken, tokens.accessToken);
  safeWrite(storageKeys.refreshToken, tokens.refreshToken);
  safeWrite(storageKeys.email, tokens.email);
  safeWrite(storageKeys.userId, tokens.userId);
};

const updateTokens = (next: Partial<AuthTokenState>) => {
  let didChange = false;
  const updated = { ...tokens };

  (Object.keys(next) as Array<keyof AuthTokenState>).forEach((key) => {
    if (typeof next[key] === "undefined") {
      return;
    }
    const value = next[key] ?? null;
    if (updated[key] !== value) {
      didChange = true;
      updated[key] = value;
    }
  });

  if (!didChange) {
    return;
  }

  tokens = updated;
  persist();
  notify();
};

const resetTokens = () => {
  tokens = {
    accessToken: null,
    refreshToken: null,
    email: null,
    userId: null,
  };
  persist();
  notify();
};

export const authTokenStore = {
  getTokens: (): AuthTokenState => ({ ...tokens }),
  getAccessToken: (): string | null => tokens.accessToken,
  getRefreshToken: (): string | null => tokens.refreshToken,
  getEmail: (): string | null => tokens.email,
  getUserId: (): string | null => tokens.userId,
  setTokens: (updates: Partial<AuthTokenState>) => updateTokens(updates),
  clearTokens: () => resetTokens(),
  getAuthHeader: (): Record<string, string> =>
    tokens.accessToken
      ? {
          Authorization: `Bearer ${tokens.accessToken}`,
        }
      : {},
  subscribe: (listener: TokenListener): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
